import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import apiService from '../services/api';

const Leaderboard = () => {
  const [activeTab, setActiveTab] = useState('daily');
  const [dailyLeaderboard, setDailyLeaderboard] = useState([]);
  const [monthlyLeaderboard, setMonthlyLeaderboard] = useState([]);
  const [monthlyMeta, setMonthlyMeta] = useState(null);
  const [allTimeLeaderboard, setAllTimeLeaderboard] = useState([]);
  const [countryLeaderboard, setCountryLeaderboard] = useState([]);
  const [myRank, setMyRank] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadLeaderboardData();
  }, []);

  const loadLeaderboardData = async () => {
    try {
      setLoading(true);

      // Load only daily and monthly leaderboards
      const [daily, monthly] = await Promise.all([
        apiService.getDailyLeaderboard(),
        apiService.getMonthlyLeaderboard(),
      ]);

      setDailyLeaderboard(daily.leaderboard || daily);
      setMonthlyLeaderboard(monthly.leaderboard || monthly);
      setMonthlyMeta({
        month: monthly.month,
        year: monthly.year,
        month_name: monthly.month_name
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return `#${rank}`;
  };

  const getCountryFlag = (country) => {
    const flags = {
      'Bangladesh': '🇧🇩',
      'Pakistan': '🇵🇰',
      'India': '🇮🇳',
      'Turkey': '🇹🇷',
      'Egypt': '🇪🇬',
      'Saudi Arabia': '🇸🇦',
      'Malaysia': '🇲🇾',
      'Indonesia': '🇮🇩',
      'UAE': '🇦🇪',
      'Qatar': '🇶🇦',
      'Kuwait': '🇰🇼',
      'Jordan': '🇯🇴',
      'Lebanon': '🇱🇧',
      'Morocco': '🇲🇦',
      'Algeria': '🇩🇿',
      'Tunisia': '🇹🇳',
    };
    return flags[country] || '🌍';
  };

  const safeNumber = (value, defaultValue = 0) => {
    if (value === null || value === undefined || isNaN(value)) {
      return defaultValue;
    }
    return Number(value);
  };

  const formatNumber = (value, defaultValue = 0) => {
    const num = safeNumber(value, defaultValue);
    return num.toLocaleString();
  };

  const LeaderboardTable = ({ data, type }) => (
    <div className="leaderboard-table">
      <div className="table-header">
        <div className="rank-col">Rank</div>
        <div className="name-col">Name</div>
        <div className="country-col">Country</div>
        <div className="points-col">Points</div>
        {type !== 'country' && <div className="stats-col">Stats</div>}
      </div>
      
      {(data || []).map((entry, index) => (
        <motion.div
          key={entry.phone || entry.country}
          className={`table-row ${entry.rank <= 3 ? 'top-three' : ''}`}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: index * 0.1 }}
        >
          <div className="rank-col">
            <span className="rank-badge">
              {getRankIcon(entry.rank)}
            </span>
          </div>
          <div className="name-col">
            <strong>{entry.name || entry.country}</strong>
            {type === 'country' && (
              <div className="user-count">{safeNumber(entry.totalUsers)} users</div>
            )}
          </div>
          <div className="country-col">
            {type !== 'country' ? (
              <>
                {getCountryFlag(entry.country)} {entry.country}
              </>
            ) : (
              <span>Avg: {safeNumber(entry.averagePoints)} pts/user</span>
            )}
          </div>
          <div className="points-col">
            <span className="points">{formatNumber(entry.total_points)}</span>
          </div>
          {type !== 'country' && (
            <div className="stats-col">
              {type === 'daily' && (
                <div className="stat-items">
                  <span>📞 {safeNumber(entry.total_calls)}</span>
                  <span>🤝 {safeNumber(entry.total_helped)}</span>
                  {entry.wakeup_count > 0 && <span>🌅 Woke Up</span>}
                </div>
              )}
              {type === 'monthly' && (
                <div className="stat-items">
                  <span>📞 {safeNumber(entry.total_calls)}</span>
                  <span>🤝 {safeNumber(entry.total_helped)}</span>
                  <span>🌅 {safeNumber(entry.wakeup_count)} days</span>
                </div>
              )}
              {type === 'alltime' && (
                <div className="stat-items">
                  <span>🌅 {safeNumber(entry.totalDaysWokeUp)} days</span>
                  <span>🤝 {safeNumber(entry.totalDaysHelped)} helped</span>
                  <span>📅 Joined {formatDate(entry.joinedDate)}</span>
                </div>
              )}
            </div>
          )}
        </motion.div>
      ))}
    </div>
  );

  if (loading) return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="loading">Loading leaderboards...</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="leaderboard-page">
      <style jsx>{`
        .leaderboard-page {
          max-width: 1200px;
          margin: 0 auto;
          padding: 20px;
          font-family: 'Inter', sans-serif;
        }
        
        .header {
          text-align: center;
          margin-bottom: 30px;
        }
        
        .header h1 {
          color: #2563eb;
          margin-bottom: 10px;
          font-size: 2.5rem;
        }
        
        .header p {
          color: #64748b;
          font-size: 1.1rem;
        }
        
        .stats-overview {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 20px;
          margin-bottom: 30px;
        }
        
        .stat-card {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          padding: 20px;
          border-radius: 12px;
          text-align: center;
          box-shadow: 0 5px 20px rgba(102, 126, 234, 0.3);
        }
        
        .stat-card.success {
          background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
        }
        
        .stat-card.warning {
          background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
        }
        
        .stat-number {
          font-size: 2rem;
          font-weight: bold;
          margin-bottom: 5px;
        }
        
        .stat-label {
          font-size: 0.9rem;
          opacity: 0.9;
        }
        
        .my-rank-card {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          padding: 25px;
          border-radius: 15px;
          margin-bottom: 30px;
          text-align: center;
        }
        
        .my-rank-card h3 {
          margin-bottom: 15px;
          font-size: 1.3rem;
        }
        
        .rank-info {
          display: flex;
          justify-content: space-around;
          flex-wrap: wrap;
          gap: 15px;
        }
        
        .rank-item {
          flex: 1;
          min-width: 120px;
        }
        
        .rank-item .rank-number {
          font-size: 2rem;
          font-weight: bold;
          margin-bottom: 5px;
        }
        
        .rank-item .rank-label {
          font-size: 0.9rem;
          opacity: 0.9;
        }
        
        .tabs {
          display: flex;
          background: #f1f5f9;
          border-radius: 12px;
          padding: 4px;
          margin-bottom: 30px;
          flex-wrap: wrap;
        }
        
        .tab {
          flex: 1;
          min-width: 120px;
          padding: 12px 20px;
          background: transparent;
          border: none;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          color: #64748b;
        }
        
        .tab.active {
          background: white;
          color: #2563eb;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }
        
        .tab:hover:not(.active) {
          background: rgba(255,255,255,0.5);
        }
        
        .leaderboard-container {
          background: white;
          border-radius: 15px;
          box-shadow: 0 5px 20px rgba(0,0,0,0.1);
          overflow: hidden;
        }
        
        .leaderboard-table {
          width: 100%;
        }
        
        .table-header {
          display: grid;
          grid-template-columns: 80px 1fr 150px 120px 200px;
          background: #f8fafc;
          padding: 15px 20px;
          font-weight: 600;
          color: #475569;
          font-size: 0.9rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        
        .table-row {
          display: grid;
          grid-template-columns: 80px 1fr 150px 120px 200px;
          padding: 15px 20px;
          border-bottom: 1px solid #e2e8f0;
          transition: all 0.3s ease;
          align-items: center;
        }
        
        .table-row:hover {
          background: #f8fafc;
        }
        
        .table-row.top-three {
          background: linear-gradient(90deg, #fef3c7, #fef9e7);
        }
        
        .rank-badge {
          font-size: 1.2rem;
          font-weight: bold;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 40px;
          height: 40px;
          border-radius: 50%;
          background: #e2e8f0;
          color: #475569;
        }
        
        .top-three .rank-badge {
          font-size: 1.5rem;
          background: transparent;
        }
        
        .points {
          font-weight: bold;
          color: #059669;
          font-size: 1.1rem;
        }
        
        .stat-items {
          display: flex;
          flex-direction: column;
          gap: 4px;
          font-size: 0.85rem;
          color: #64748b;
        }
        
        .user-count {
          font-size: 0.8rem;
          color: #64748b;
          margin-top: 2px;
        }
        
        .error {
          background: #fee2e2;
          color: #dc2626;
          padding: 15px;
          border-radius: 10px;
          margin-bottom: 20px;
          border: 1px solid #fecaca;
        }
        
        .loading {
          text-align: center;
          padding: 50px;
          font-size: 1.2rem;
          color: #64748b;
        }
        
        .empty-state {
          text-align: center;
          padding: 50px;
          color: #64748b;
        }
        
        .empty-state h3 {
          margin-bottom: 10px;
          color: #475569;
        }
        
        @media (max-width: 768px) {
          .leaderboard-page {
            padding: 15px;
          }
          
          .stats-overview {
            grid-template-columns: repeat(2, 1fr);
          }
          
          .tabs {
            flex-direction: column;
          }
          
          .tab {
            flex: none;
          }
          
          .table-header,
          .table-row {
            grid-template-columns: 60px 1fr 100px 80px;
            font-size: 0.85rem;
          }
          
          .stats-col {
            display: none;
          }
          
          .rank-info {
            flex-direction: column;
            gap: 10px;
          }
        }
      `}</style>


      <div className="tabs">
        <button
          className={`tab ${activeTab === 'daily' ? 'active' : ''}`}
          onClick={() => setActiveTab('daily')}
        >
          📅 Today
        </button>
        <button
          className={`tab ${activeTab === 'monthly' ? 'active' : ''}`}
          onClick={() => setActiveTab('monthly')}
        >
          📊 This Month
        </button>
      </div>

      <div className="leaderboard-container">
        {activeTab === 'daily' && (
          dailyLeaderboard.length > 0 ?
            <LeaderboardTable data={dailyLeaderboard} type="daily" /> :
            <div className="empty-state">
              <h3>📅 Daily Leaderboard</h3>
              <p>No activity recorded for today yet. Be the first to wake up for Fajr!</p>
            </div>
        )}

        {activeTab === 'monthly' && (
          <>
            {monthlyMeta && (
              <div style={{ textAlign: 'center', marginBottom: '20px', color: '#667eea', fontSize: '1.5rem', fontWeight: 'bold' }}>
                📊 {monthlyMeta.month_name} {monthlyMeta.year}
              </div>
            )}
            {monthlyLeaderboard.length > 0 ?
              <LeaderboardTable data={monthlyLeaderboard} type="monthly" /> :
              <div className="empty-state">
                <h3>📊 Monthly Leaderboard</h3>
                <p>No activity recorded for this month yet.</p>
              </div>
            }
          </>
        )}
      </div>
      <Footer />
      </div>
    </div>
  );
};

export default Leaderboard;